// MUI-free copy of Ui-components/Decoding.
import {memo} from 'react';
import type {CSSProperties, ReactNode} from 'react';
import SocialMedia from '@/components/plain-sections/SocialMedia';
import type {StaticImageData} from 'next/image';
import parse from 'html-react-parser';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import ArrowButton from '@/components/atoms/ArrowButton';
import styles from './Decoding.module.scss';

interface DecodingProps {
    imageSrc: StaticImageData;
    alt?: string;
    heading: ReactNode;
    subHeading?: string[];
    buttonText?: string;
    openForm?: () => void;
    className?: string;
    isSocialMediaBtn?: boolean;
    imgClass?: string;
    /** Inline styles for the image box. Flat CSS only — selector or media-query rules need `imageBoxClassName`. */
    imageBoxSx?: CSSProperties;
    /** Inline styles for the <img>. Flat CSS only — selector or media-query rules need `imgClass`. */
    imgSx?: CSSProperties;
    /** Extra class on the image box (stand-in for responsive / nested `imageBoxSx` objects). */
    imageBoxClassName?: string;
}

// The original also passed a `decodingImgResponsiveSx` (≤768px width 500px, ≤430px 100%) to the image. It never
// rendered: the global `.decoding-img { width: 100% !important }` loads after Emotion's styles and won every tie,
// so it is left out here rather than re-added as a rule that would now win.
const Decoding = ({
    imageSrc,
    alt = '',
    heading,
    subHeading,
    buttonText,
    openForm,
    className,
    isSocialMediaBtn,
    imgClass,
    imageBoxSx,
    imgSx,
    imageBoxClassName
}: DecodingProps) => (
    <div className={`decoding-wrapper ${className}`}>
        <div className={`banner-container-grid ${styles.grid}`}>
            <div className={`${styles.item} ${styles.imageItem}`}>
                <div className={imageBoxClassName ? `justify-center ${imageBoxClassName}` : 'justify-center'} style={imageBoxSx}>
                    <OptimizedImage
                        src={imageSrc}
                        alt={alt}
                        className={`banner-img decoding-img ${imgClass || ''}`}
                        style={imgSx}
                        priority={false}
                    />
                </div>
            </div>
            <div className={styles.item}>
                <div>
                    <h2 className={`font-headers ${styles.heading}`}>{heading}</h2>
                    {subHeading?.map((item: string, index: number) => (
                        <div key={index + 1}>
                            <div className={`font-regular-text remove-link-underline ${styles.subHeading}`}>{item ? parse(item) : ''}</div>
                        </div>
                    ))}
                    {buttonText && (
                        <ArrowButton variant="primary" onClick={openForm}>
                            {buttonText}
                        </ArrowButton>
                    )}
                    {isSocialMediaBtn && <SocialMedia className="decoding-social-media" />}
                </div>
            </div>
        </div>
    </div>
);

export default memo(Decoding);
